// Browser side of the mark_topic outline signal (LUM-134).
//
// The relay registers the tool (flag-gated, worker/lib/realtime/
// topics.ts) and acks every call itself; the browser only READS the
// forwarded calls to drive the interview outline. Semantics:
// mark_topic(id) = "id is now COMPLETE".

import { z } from 'zod';

/** Must match MARK_TOPIC_TOOL_NAME in worker/lib/realtime/topics.ts. */
const MARK_TOPIC_TOOL_NAME = 'mark_topic';

const MarkTopicItemDoneSchema = z.object({
  type: z.literal('response.output_item.done'),
  item: z.object({
    type: z.literal('function_call'),
    name: z.literal(MARK_TOPIC_TOOL_NAME),
    // A call cut off by a barge-in carries partial arguments — never
    // mark from it.
    status: z.literal('completed'),
    arguments: z.string(),
  }),
});

const MarkTopicArgsSchema = z.object({ topic_id: z.string().min(1) });

/**
 * The topic id a completed mark_topic call names, or null for any other
 * event. Read from response.output_item.done — it lands as soon as the
 * call is generated, well inside the outline's ~1s budget, while the
 * spoken signpost of the same response is still playing.
 */
export function markedTopicIdFromEvent(
  event: Record<string, unknown>,
): string | null {
  const done = MarkTopicItemDoneSchema.safeParse(event);
  if (!done.success) return null;
  let args: unknown;
  try {
    args = JSON.parse(done.data.item.arguments);
  } catch {
    return null;
  }
  const parsed = MarkTopicArgsSchema.safeParse(args);
  return parsed.success ? parsed.data.topic_id : null;
}

/**
 * Accumulate a mark: completion is monotonic — a revisit of an earlier
 * topic never removes a later one. Returns the SAME array when the id is
 * already marked, so a repeat mark is a React no-op.
 */
export function recordMarkedTopic(
  marked: readonly string[],
  topicId: string,
): readonly string[] {
  return marked.some(id => id === topicId) ? marked : [...marked, topicId];
}
